interface SiteLaunchResponse {
  readonly launch_url: string;
}

type SiteLaunchResult =
  { readonly ok: true } | { readonly ok: false; readonly cause: unknown };

/**
 * Mint a Site session, then hand the current tab to the exact-origin launch
 * URL returned by AppNotes. Browser Back returns the reviewer to AppNotes.
 */
export async function launchSiteReview(input: {
  readonly mint: () => Promise<SiteLaunchResponse>;
  readonly navigate: (url: string) => void;
}): Promise<SiteLaunchResult> {
  try {
    const launch = await input.mint();
    input.navigate(launch.launch_url);
    return { ok: true };
  } catch (cause) {
    return { ok: false, cause };
  }
}

/** The part of a browser tab that a review launch needs. */
export interface ReviewTab {
  opener: unknown;
  readonly location: { replace(url: string): void };
  close(): void;
}

/**
 * Open a blank tab synchronously inside the click, so a popup blocker does not
 * eat it, then mint the launch link and point that tab at it. The tab loses
 * its opener before it leaves the AppNotes origin.
 */
export function launchSiteReviewInNewTab(input: {
  readonly open: () => ReviewTab | null;
  readonly mint: () => Promise<SiteLaunchResponse>;
}):
  | { readonly blocked: true }
  | { readonly blocked: false; readonly result: Promise<SiteLaunchResult> } {
  const tab = input.open();
  if (tab === null) return { blocked: true };
  return {
    blocked: false,
    result: (async (): Promise<SiteLaunchResult> => {
      try {
        const launch = await input.mint();
        tab.opener = null;
        tab.location.replace(launch.launch_url);
        return { ok: true };
      } catch (cause) {
        tab.close();
        return { ok: false, cause };
      }
    })(),
  };
}
